import { normalizeToMonthly } from '../utils/finance'; 
import { DEFAULT_DATA } from '../utils/constants';

export const useDataActions = (data, setData) => {
    
    // --- ACTIONS ---
    const update = (field, value) => {
        const safeValue = typeof value === 'number' && isNaN(value) ? 0 : value;
        setData(prev => ({ ...prev, [field]: safeValue }));
    };
    
    const setTrackerOffset = (newOffset) => {
        setData(prev => {
            const val = typeof newOffset === 'function' 
                ? newOffset(prev.trackerOffset || 0) 
                : newOffset;
            return { ...prev, trackerOffset: val };
        });
    };

    const updateArrayItem = (arrayName, id, field, value) => { 
        const safeValue = typeof value === 'number' && isNaN(value) ? 0 : value;
        setData(prev => ({ 
            ...prev, 
            [arrayName]: prev[arrayName].map(item => {
                if (item.id !== id) return item;
                const isAssetUpdate = arrayName === 'assets' && (
                    field === 'value' || field === 'returnRate' || field === 'name' || !item.lastUpdated
                );
                const extra = isAssetUpdate ? { lastUpdated: new Date().toISOString() } : {};
                return { ...item, [field]: safeValue, ...extra };
            }) 
        })); 
    };
    
    const addArrayItem = (arrayName, newItem) => { 
        let itemWithMeta = newItem;
        if (arrayName === 'assets') {
            itemWithMeta = { ...newItem, lastUpdated: new Date().toISOString() };
        } 
        setData(prev => ({ ...prev, [arrayName]: [...prev[arrayName], itemWithMeta] })); 
    };

    // --- FIX: Allow Category and Target Date overrides ---
    const recordQuickTransaction = (desc, amount, isCredit = false, isIncome = false, category = null, targetDate = null) => {
        setData(prev => {
            // Logic: Use provided category, or fallback to default based on flow direction
            const finalCategory = category 
                ? category 
                : (isIncome ? "Income" : "Uncategorized");

            const newItem = {
                id: Date.now(), 
                // Fix: Use targetDate if provided (for future/past adds), otherwise Now
                date: targetDate ? targetDate : new Date().toISOString(), 
                desc: desc || (isIncome ? "Quick Inflow" : "Quick Expense"), 
                amount: Math.abs(amount), 
                category: finalCategory, 
                isCredit: isCredit, 
                paidOff: !isCredit,
                isInflow: isIncome
            };
            
            return {
                ...prev,
                transactions: [...prev.transactions, newItem]
            };
        });
    };
    
    const removeArrayItem = (arrayName, id) => { 
        setData(prev => {
            const removedItem = prev[arrayName]?.find(item => item.id === id);
            const newState = { ...prev, [arrayName]: prev[arrayName].filter(item => item.id !== id) };
            
            if (arrayName === 'expenseItems' && removedItem) {
                if (removedItem.linkedSinkingFundId) {
                    newState.sinkingFunds = newState.sinkingFunds.map(sf => 
                        sf.id === removedItem.linkedSinkingFundId ? { ...sf, isCommitted: false } : sf
                    );
                }
            }

            if (arrayName === 'sinkingFunds') {
                newState.expenseItems = newState.expenseItems.filter(ex => ex.linkedSinkingFundId !== id);
            }

            return newState;
        }); 
    };
    
    const updateAssetAlloc = (id, type, val) => { 
        const safeVal = parseFloat(val) || 0;
        setData(prev => ({ 
            ...prev, 
            assets: prev.assets.map(a => a.id === id 
                ? { ...a, lastUpdated: new Date().toISOString(), allocation: { ...a.allocation, [type]: safeVal } } 
                : a) 
        })); 
    };

    const markVisionReviewed = () => {
        setData(prev => ({ ...prev, lastVisionReview: new Date().toISOString() }));
    };

    const markTourComplete = () => {
        setData(prev => ({ ...prev, hasSeenTour: true }));
    };

    const recordEvent = (eventName) => {
        setData(prev => ({
            ...prev,
            flags: { ...prev.flags, [eventName]: true }
        }));
    };

    const resetBadge = (badgeId) => {
        setData(prev => {
            const existingBadges = prev.badges || [];
            const updatedBadges = existingBadges.map(b => 
                b.id === badgeId ? { ...b, earned: false, earnedDate: null } : b
            );
            return { ...prev, badges: updatedBadges };
        });
    };

    const updateProfile = (updates) => {
        setData(prev => ({ ...prev, ...updates }));
    };

    const addFriend = (friendData) => {
        setData(prev => ({ ...prev, friends: [...(prev.friends || []), friendData] }));
    };

    const removeFriend = (friendUsername) => {
        const newFriends = (data.friends || []).filter(f => f.username !== friendUsername);
        setData(prev => ({ ...prev, friends: newFriends }));
    };

    const markArticleRead = (articleId) => {
        setData(prev => {
            if (prev.readArticles?.includes(articleId)) return prev;
            const newRead = [...(prev.readArticles || []), articleId];
            return { ...prev, readArticles: newRead };
        });
    };

    const resetData = () => {
        const cleanState = { ...DEFAULT_DATA, hasSeenIntro: true }; 
        setData(cleanState);
        window.location.reload(); 
    };

    return {
        update, 
        updateArrayItem, 
        addArrayItem, 
        recordQuickTransaction,
        removeArrayItem, 
        updateAssetAlloc, 
        markVisionReviewed, 
        markTourComplete,
        recordEvent, 
        resetBadge, 
        updateProfile, 
        addFriend, 
        removeFriend, 
        markArticleRead, 
        resetData,
        setTrackerOffset
    };
};